import { type ColumnDef, type Row } from "@tanstack/react-table";
import {
  DevicesIcon,
  LaptopIcon,
  MobileIcon,
  MonitorIcon,
  TabletIcon,
} from "vega-icons";

import { type DeviceLogin } from "@vega-api/generated/types/deviceLogin";
import { DeviceLoginDeviceType } from "@vega-api/generated/types/deviceLoginDeviceType";
import {
  MAX_TEXT_COLUMN_PROPS,
  TABLE_COLUMN_WIDTHS,
} from "@vega-core/components/Table/CoreTable/constants";
import { m } from "@vega-core/paraglide/messages";
import {
  DateFormatString,
  formatDateByPattern,
} from "@vega-core/utils/formatDate";

import { formatUserAgent } from "../utils/deviceUtils";

const ICON_SIZE = "h-4 w-4 min-w-4 min-h-4";

export const getDeviceIcon = (deviceType?: DeviceLoginDeviceType) => {
  if (!deviceType) return null;

  switch (deviceType) {
    case DeviceLoginDeviceType.MOBILE:
      return <MobileIcon className={ICON_SIZE} />;
    case DeviceLoginDeviceType.TABLET:
      return <TabletIcon className={ICON_SIZE} />;
    case DeviceLoginDeviceType.DESKTOP:
      return <MonitorIcon className={ICON_SIZE} />;
    case DeviceLoginDeviceType.UNKNOWN:
      return <DevicesIcon className={ICON_SIZE} />;
    default:
      return <LaptopIcon className={ICON_SIZE} />;
  }
};

export const useDeviceLoginColumns = (): ColumnDef<
  DeviceLogin & { formattedLocation: string }
>[] => [
  {
    accessorKey: "deviceType",
    header: m.settings_login(),
    size: TABLE_COLUMN_WIDTHS.DEFAULT * 2,

    cell: ({
      row,
    }: {
      row: Row<DeviceLogin & { formattedLocation: string }>;
    }) => {
      const { deviceType, userAgent } = row.original;
      const icon = getDeviceIcon(deviceType as DeviceLoginDeviceType);

      return (
        <div className="flex items-center gap-2">
          {icon}
          <span className="whitespace-nowrap">
            {formatUserAgent(userAgent)}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "loginAt",
    header: m.emails_date_time(),
    enableSorting: false,
    cell: ({
      row,
    }: {
      row: Row<DeviceLogin & { formattedLocation: string }>;
    }) => {
      const date = row.original.loginAt;

      if (!date) return "-";

      return (
        <div className="flex flex-col">
          <div>{formatDateByPattern(date, DateFormatString.US_DATE_SLASH)}</div>
          <div className="text-text/secondary">
            {formatDateByPattern(date, DateFormatString.US_TIME)}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "formattedLocation",
    header: m.titles_location(),
    headerTooltip: m.settings_locationNote(),
    ...MAX_TEXT_COLUMN_PROPS,
    cell: ({
      row,
    }: {
      row: Row<DeviceLogin & { formattedLocation: string }>;
    }) => row.original.formattedLocation,
  },
];
